/* Developer : Nithin
Date : 26-04-2026
changes: passed isLoading prop to DataTable component */
import React, { useMemo } from "react";
import { Eye, Phone, Edit2, UserX, UserCheck } from "lucide-react";
import { DataTable, Avatar, Badge, Button, TableActionButton } from "../../../components/ui";
import { ACCOUNTS_TABLE_HEADERS as AH, STATUS_VARIANT_MAP, STATUS_COLORS } from "@/constants/globalConstants";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmt = (v) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

export const AccountTableView = ({
  accounts,
  onAccountClick,
  onEditClick,
  onToggleDisable,
  onUpdateAccount,
  updateStatus,
  onAddActivity,
  user,
  teamMembers = [],
  salesExecs = [],
  projectManagers = [],
  isLoading = false
}) => {
  const role = user?.role?.toLowerCase()?.trim()?.replace(/[-_\s]+/g, "-");
  const isSalesExec = role === 'sales-exec' || role === 'sale-exec' || role === 'sales-executive';
  const isSalesHead = role === 'sales-head' || role === 'sale-head';
  const isSuperAdmin = ['admin', 'owner', 'Verve-admin'].includes(role);

  const getStatusVariant = (status) => STATUS_VARIANT_MAP[status?.toLowerCase()] || STATUS_VARIANT_MAP.default;

  const columns = useMemo(() => {
    const cols = [
      {
        header: 'Verve ID',
        key: 'customId',
        minWidth: '100px',
        render: (l) => <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">{l.customId || '—'}</span>
      },
      {
        header: 'Customer Info',
        key: 'customerName',
        minWidth: '200px',
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ''} ${l.lastName || ''}`.trim() || 'No Name';
          const phone = l.phoneNumber || l.phone || '—';
          const email = l.email || '';
          const contactStr = email ? `${phone} | ${email}` : phone;

          return (
            <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
              <span className="text-brand-dark tracking-tight text-sm truncate" title={fullName}>{fullName}</span>
              <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
                {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
              </span>
            </div>
          );
        }
      },
      {
        header: 'Address',
        key: 'address',
        minWidth: '200px',
        render: (l) => {
          const parts = [
            l.addressLine1 || l.address1,
            l.addressLine2 || l.address2,
            l.city,
            l.district,
            l.state,
            l.pincode
          ].filter(Boolean);
          const addressText = parts.length > 0 ? parts.join(", ") : '—';
          return (
            <div className="flex flex-col min-w-0" title={addressText}>
              <span className="text-slate-600 text-[11px] truncate">{addressText}</span>
            </div>
          );
        }
      },
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Project</span>
            <span>Value (₹)</span>
          </div>
        ),
        key: 'budget',
        minWidth: '90px',
        render: (l) => <span className="text-[11px] font-medium text-slate-700">₹{fmt(l.budget || l.projectValue || 0)}</span>
      },
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Total</span>
            <span>Received (₹)</span>
          </div>
        ),
        key: 'totalReceived',
        minWidth: '90px',
        render: (l) => <span className="text-[11px] font-medium text-emerald-600">₹{fmt(l.totalReceived || 0)}</span>
      },

      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Project</span>
            <span>Expenses (₹)</span>
          </div>
        ),
        key: 'otherExpenses',
        minWidth: '90px',
        render: (l) => <span className="text-[11px] font-medium text-rose-500">₹{fmt(l.otherExpenses || 0)}</span>
      },
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Material</span>
            <span>Expenses (₹)</span>
          </div>
        ),
        key: 'materialExpenses',
        minWidth: '90px',
        render: (l) => <span className="text-[11px] font-medium text-rose-500">₹{fmt(l.materialExpenses || 0)}</span>
      },
      /*
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Total</span>
            <span>Expenses (₹)</span>
          </div>
        ),
        key: 'totalExpenses',
        minWidth: '90px',
        render: (l) => {
          const total = (Number(l.otherExpenses) || 0) + (Number(l.materialExpenses) || 0);
          return <span className="text-[11px] font-medium text-rose-600">₹{fmt(total)}</span>;
        }
      },
      */
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Balance</span>
            <span>Due (₹)</span>
          </div>
        ),
        key: 'balanceDue',
        minWidth: '90px',
        render: (l) => <span className="text-[11px] font-bold text-slate-800">₹{fmt(l.balance || 0)}</span>
      },
      /*
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Budget</span>
            <span>Left (₹)</span>
          </div>
        ),
        key: 'budgetLeft',
        minWidth: '90px',
        render: (l) => {
          const budget = Number(l.budget) || Number(l.projectValue) || 0;
          const totalExp = (Number(l.otherExpenses) || 0) + (Number(l.materialExpenses) || 0);
          const left = budget - totalExp;
          return <span className={`text-[11px] font-medium ${left < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>₹{fmt(left)}</span>;
        }
      },
      */
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Project</span>
            <span>Status</span>
          </div>
        ),
        key: 'status',
        minWidth: '90px',
        render: (l) => {
          const effectiveStatus = (l.leadStatus || l.status || 'new').toLowerCase();
          return (
            <Badge
              variant={getStatusVariant(effectiveStatus)}
              label={effectiveStatus === 'new' ? 'New Account' : effectiveStatus.replace('_', ' ')}
              className="min-w-[70px] justify-center tracking-tighter text-[9px] px-2 py-0.5 rounded-full border shadow-sm"
            />
          );
        }
      },
      {
        header: (
          <div className="flex flex-col leading-tight">
            <span>Payment</span>
            <span>Status</span>
          </div>
        ),
        key: 'paymentStatus',
        minWidth: '80px',
        render: (l) => (
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${l.paymentStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {l.paymentStatus || 'Pending'}
          </span>
        )
      },
      /*
      {
        header: AH.EXPENSE_STATUS,
        key: 'expenseStatus',
        minWidth: '140px',
        render: (l) => (
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${l.expenseStatus === 'Settled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
            {l.expenseStatus || 'Pending'}
          </span>
        )
      },
      */
      {
        header: AH.ACTIONS,
        key: 'actions',
        minWidth: '60px',
        render: (l) => (
          <div className="flex items-center justify-center">
            <TableActionButton
              icon={Eye}
              variant="primary"
              onClick={(e) => { e.stopPropagation(); onAccountClick(l); }}
              title="View Details"
            />
          </div>
        )
      }
    ];

    return cols;

  }, [isSalesExec, isSalesHead, isSuperAdmin, onAddActivity, onEditClick, onAccountClick, onToggleDisable, onUpdateAccount, projectManagers, salesExecs, getStatusVariant]);

  return (
    <DataTable
      columns={columns}
      data={accounts}
      isLoading={isLoading}
      backgroundImage={TABLE_BACKGROUNDS.accounts}
      // onRowClick={(idx, row) => onAccountClick(row)}
      getRowClassName={(idx, row) => row?.isDisabled ? "opacity-50 bg-slate-50/50" : ""}
      className="border-none shadow-none rounded-none"
    />
  );
};
